/**
 * What the rail renders and whether the user may change it here.
 *
 * In a browser tab and the root app the rail is the star set. Inside an
 * installed area app it is that app's `APP_NAV` membership, fixed: no drag, no
 * Unpin, no Move — a menu here used to write the GLOBAL star set, so unpinning
 * Drafts in the Inbox app removed it from the browser's rail too. Per-app star
 * sets over the membership are phase 2 of the spec.
 *
 * One hook so the sidebar and the phone's bottom bar can never disagree.
 * Spec: docs/plans/2026-09-16-pwa-app-nav-design.md §4.3.
 */
import { useMemo } from 'react';

import { APP_NAV } from '@shared/app-identity/appNav';
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';

import { useStars } from './useStars';

export interface Rail {
  /** Destination ids in rail order. */
  ids: readonly string[];
  /** Whether drag, Move and Unpin apply. False inside an installed app. */
  editable: boolean;
}

/** The running rail: stars in a browser tab, the app's membership inside an installed app. */
export function useRail(): Rail {
  const installedApp = useInstalledApp();
  const { starredIds } = useStars();
  return useMemo(
    () =>
      installedApp === null
        ? { ids: starredIds, editable: true }
        : { ids: APP_NAV[installedApp], editable: false },
    [installedApp, starredIds]
  );
}
